import React, { useState } from "react";
import { Box, Paper, Typography, Stack } from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import Sidebar from "./AdminPanel/Sidebar";
import TopBar from "./AdminPanel/TopBar";
import { useDashboardTheme } from "../../context/DashboardThemeContext";
import { useAuth } from "../../context/AuthContext";

function userCan(user, code) {
  if (!user) return false;
  if (user.role === "super_admin" || user.role === "admin") return true;
  return (user.permissions || []).includes(code);
}

export default function AdminPanel() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const quickAccess = [
    {
      title: "Nueva Factura",
      icon: <RequestQuoteRoundedIcon />,
      path: "/admin-panel/facturacion",
      permission: "ventas.view",
      metal:
        "linear-gradient(135deg, #ECFEFF 0%, #67E8F9 24%, #0891B2 55%, #164E63 78%, #ECFEFF 100%)",
      glow: "rgba(8,145,178,0.24)",
    },
    {
      title: "Documentos",
      icon: <DescriptionRoundedIcon />,
      path: "/admin-panel/documentos",
      permission: "ventas.view",
      metal:
        "linear-gradient(135deg, #EEF2FF 0%, #A5B4FC 24%, #4F46E5 55%, #312E81 78%, #EEF2FF 100%)",
      glow: "rgba(79,70,229,0.24)",
    },
    {
      title: "Estadísticas",
      icon: <QueryStatsRoundedIcon />,
      path: "/admin-panel/estadisticas",
      permission: "ventas.view",
      metal:
        "linear-gradient(135deg, #F0FDF4 0%, #86EFAC 24%, #16A34A 55%, #14532D 78%, #F0FDF4 100%)",
      glow: "rgba(22,163,74,0.24)",
    },
    {
      title: "Productos",
      icon: <Inventory2RoundedIcon />,
      path: "/admin-panel/productos",
      permission: "products.view",
      metal:
        "linear-gradient(135deg, #F8FAFC 0%, #CBD5E1 22%, #64748B 52%, #1E293B 80%, #F8FAFC 100%)",
      glow: "rgba(71,85,105,0.24)",
    },
    {
      title: "Personas",
      icon: <PeopleAltRoundedIcon />,
      path: "/admin-panel/personas",
      permission: "persons.view",
      metal:
        "linear-gradient(135deg, #FDF4FF 0%, #E879F9 24%, #A21CAF 55%, #581C87 78%, #FDF4FF 100%)",
      glow: "rgba(162,28,175,0.24)",
    },
    {
      title: "Órdenes",
      icon: <ReceiptLongRoundedIcon />,
      path: "/admin-panel/ordenes",
      permission: "ventas.view",
      metal:
        "linear-gradient(135deg, #FFF7ED 0%, #FDBA74 24%, #EA580C 55%, #7C2D12 78%, #FFF7ED 100%)",
      glow: "rgba(234,88,12,0.24)",
    },
  ].filter((item) => userCan(user, item.permission));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            px: { xs: 1.5, sm: 2.5, md: 4 },
            py: { xs: 2.5, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "90%",
              mx: "auto",
              mb: { xs: 3, md: 4 },
              px: { xs: 2.2, sm: 3, md: 4 },
              py: { xs: 3, md: 4 },
              borderRadius: { xs: "24px", md: "32px" },
              bgcolor: dashboardTheme.sidebar_color,
              color: dashboardTheme.sidebar_text_color,
              border: `1px solid ${dashboardTheme.border_color}`,
              boxShadow:
                dashboardTheme.theme_mode === "dark"
                  ? "0 22px 55px rgba(0,0,0,0.32)"
                  : "0 22px 55px rgba(15,23,42,0.12)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 190,
                height: 190,
                borderRadius: "50%",
                right: -65,
                top: -75,
                bgcolor: "rgba(255,255,255,0.08)",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                width: 120,
                height: 120,
                borderRadius: "50%",
                left: -40,
                bottom: -50,
                bgcolor: "rgba(255,255,255,0.06)",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: 25, sm: 31, md: 39 },
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: "-0.03em",
                  color: dashboardTheme.sidebar_text_color,
                }}
              >
                Bienvenido a FACTUSYSDR
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              width: "90%",
              mx: "auto",
              borderRadius: "28px",
              border: `1px solid ${dashboardTheme.border_color}`,
              bgcolor: dashboardTheme.card_background,
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 2.5, md: 3.5 },
              boxShadow:
                dashboardTheme.theme_mode === "dark"
                  ? "none"
                  : "0 14px 38px rgba(17,24,39,0.08)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 20, md: 23 },
                fontWeight: 700,
                color: dashboardTheme.text_color,
                mb: { xs: 2.5, md: 3 },
                textAlign: "center",
              }}
            >
              Accesos rápidos
            </Typography>

            <Box
              sx={{
                width: "100%",
                maxWidth: 1080,
                mx: "auto",
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 300px))",
                  lg: "repeat(3, minmax(0, 300px))",
                },
                gap: { xs: 2.2, sm: 2.6, md: 3.2 },
                justifyContent: "center",
                alignItems: "stretch",
              }}
            >
              {quickAccess.map((item) => (
                <Paper
                  key={item.title}
                  elevation={0}
                  onClick={() => navigate(item.path)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "22px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: dashboardTheme.card_background,
                    p: { xs: 2, md: 2.2 },
                    minHeight: 150,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition:
                      "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      opacity: dashboardTheme.theme_mode === "dark" ? 0.24 : 0.17,
                      background: item.metal,
                      pointerEvents: "none",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: 105,
                      height: 105,
                      borderRadius: "50%",
                      right: -38,
                      bottom: -42,
                      background:
                        dashboardTheme.theme_mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(255,255,255,0.55)",
                      filter: "blur(2px)",
                      pointerEvents: "none",
                    },
                    "&:hover": {
                      transform: "translateY(-5px)",
                      borderColor: dashboardTheme.primary_color,
                      boxShadow:
                        dashboardTheme.theme_mode === "dark"
                          ? `0 20px 45px ${item.glow}`
                          : `0 20px 45px ${item.glow}`,
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ position: "relative", zIndex: 1 }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#FFFFFF",
                        background: item.metal,
                        boxShadow:
                          dashboardTheme.theme_mode === "dark"
                            ? `0 12px 24px ${item.glow}`
                            : `0 12px 24px ${item.glow}`,
                        "& svg": {
                          fontSize: 28,
                          filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))",
                        },
                      }}
                    >
                      {item.icon}
                    </Box>

                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: dashboardTheme.primary_color,
                        bgcolor:
                          dashboardTheme.theme_mode === "dark"
                            ? "rgba(255,255,255,0.06)"
                            : "rgba(17,24,39,0.04)",
                        border: `1px solid ${dashboardTheme.border_color}`,
                        transition: "transform 0.22s ease",
                      }}
                    >
                      <ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </Stack>

                  <Box sx={{ position: "relative", zIndex: 1, mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 18, md: 19 },
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                        lineHeight: 1.2,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Paper>
              ))}

              {quickAccess.length === 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    maxWidth: 520,
                    mx: "auto",
                    p: 3,
                    borderRadius: "22px",
                    bgcolor: dashboardTheme.card_background,
                    border: `1px solid ${dashboardTheme.border_color}`,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: dashboardTheme.muted_text_color,
                    }}
                  >
                    No tienes accesos disponibles.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}